<template>
  <transition name="overlay-fade">
    <div v-if="isVisible" class="transition-overlay" :class="{ 'dark-mode': isDark }" :style="overlayStyle">
      <div class="overlay-content">
        <!-- Module Icon and Header -->
        <div class="module-header">
          <div class="module-icon" :style="{ background: moduleColor }">
            <i :class="moduleIcon"></i>
          </div>
          <h3 class="module-title">{{ moduleTitle }}</h3>
        </div>

        <!-- Loading Message -->
        <div class="loading-message">
          {{ loadingMessage }}
        </div>



        <!-- Skeleton Loader - 4Task Layout -->
        <div class="skeleton-container">
          <div class="skeleton-workspace">
            <!-- Task Dashboard Header -->
            <div class="skeleton-header">
              <div class="skeleton-line full"></div>
              <div class="skeleton-actions">
                <div class="skeleton-button"></div>
                <div class="skeleton-button"></div>
                <div class="skeleton-button"></div>
              </div>
            </div>

            <!-- Main Content Area -->
            <div class="skeleton-content">
              <!-- Sidebar with hierarchical structure -->
              <div class="skeleton-sidebar">
                <div class="skeleton-workspace-title">
                  <div class="skeleton-line medium"></div>
                </div>
                <div class="skeleton-space-item">
                  <div class="skeleton-line long"></div>
                  <div class="skeleton-nested">
                    <div class="skeleton-line medium"></div>
                    <div class="skeleton-line short"></div>
                  </div>
                </div>
                <div class="skeleton-space-item">
                  <div class="skeleton-line long"></div>
                </div>
                <div class="skeleton-space-item">
                  <div class="skeleton-line medium"></div>
                </div>
              </div>

              <!-- Task List Area -->
              <div class="skeleton-main">
                <div class="skeleton-task-header">
                  <div class="skeleton-line long"></div>
                  <div class="skeleton-filters">
                    <div class="skeleton-button small"></div>
                    <div class="skeleton-button small"></div>
                  </div>
                </div>
                <div class="skeleton-task-list">
                  <div class="skeleton-task-row">
                    <div class="skeleton-checkbox"></div>
                    <div class="skeleton-task-content">
                      <div class="skeleton-line full"></div>
                      <div class="skeleton-line medium"></div>
                    </div>
                    <div class="skeleton-task-meta">
                      <div class="skeleton-line short"></div>
                    </div>
                  </div>
                  <div class="skeleton-task-row">
                    <div class="skeleton-checkbox"></div>
                    <div class="skeleton-task-content">
                      <div class="skeleton-line long"></div>
                      <div class="skeleton-line short"></div>
                    </div>
                    <div class="skeleton-task-meta">
                      <div class="skeleton-line short"></div>
                    </div>
                  </div>
                  <div class="skeleton-task-row">
                    <div class="skeleton-checkbox"></div>
                    <div class="skeleton-task-content">
                      <div class="skeleton-line full"></div>
                      <div class="skeleton-line medium"></div>
                    </div>
                    <div class="skeleton-task-meta">
                      <div class="skeleton-line short"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Progress Indicator -->
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
          <div class="progress-text">
            <span v-if="progress === 100" class="success-icon">✓</span>
            {{ progressText }}
          </div>
        </div>

        <!-- Animated Dots -->
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

export default {
  name: 'TransitionOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    moduleTitle: {
      type: String,
      default: 'Cargando módulo'
    },
    moduleIcon: {
      type: String,
      default: 'ri-loader-line'
    },
    moduleColor: {
      type: String,
      default: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    },
    loadingMessage: {
      type: String,
      default: 'Preparando interfaz...'
    },
    isDark: {
      type: Boolean,
      default: false
    },
    autoProgress: {
      type: Boolean,
      default: true
    },
    duration: {
      type: Number,
      default: 1500
    }
  },
  emits: ['loading-complete'],
  setup(props, { emit }) {
    const progress = ref(0)
    const progressText = ref('Inicializando...')
    let progressInterval = null


    const progressSteps = [
      { percent: 25, text: 'Verificando permisos...' },
      { percent: 50, text: 'Preparando workspace...' },
      { percent: 75, text: 'Abriendo nueva pestaña...' },
      { percent: 100, text: '¡Listo! Redirigiendo...' }
    ]

    const startProgress = () => {
      if (!props.autoProgress) return

      progress.value = 0
      progressText.value = 'Inicializando...'

      let currentStep = 0
      const stepDuration = props.duration / progressSteps.length

      progressInterval = setInterval(() => {
        if (currentStep < progressSteps.length) {
          const step = progressSteps[currentStep]
          progress.value = step.percent
          progressText.value = step.text
          currentStep++

          if (currentStep === progressSteps.length) {
            // Add success animation class
            setTimeout(() => {
              const overlayContent = document.querySelector('.overlay-content')
              if (overlayContent) {
                overlayContent.classList.add('completing')
              }

              // Emit completion after success animation
              setTimeout(() => {
                emit('loading-complete')
              }, 300)
            }, 200)
          }
        }
      }, stepDuration)
    }

    const stopProgress = () => {
      if (progressInterval) {
        clearInterval(progressInterval)
        progressInterval = null
      }
    }

    watch(() => props.isVisible, (newValue) => {
      if (newValue) {
        startProgress()
      } else {
        stopProgress()
      }
    })

    onMounted(() => {
      if (props.isVisible) {
        startProgress()
      }
    })

    onUnmounted(() => {
      stopProgress()
    })

    const overlayStyle = computed(() => {
      const baseStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100010,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease'
      }

      if (props.isDark) {
        return {
          ...baseStyle,
          background: 'rgba(17, 24, 39, 0.98)'
        }
      } else {
        return {
          ...baseStyle,
          background: 'rgba(255, 255, 255, 0.95)'
        }
      }
    })

    return {
      progress,
      progressText,
      overlayStyle
    }
  }
}
</script>

<style scoped>
/* Overlay styles are now handled via inline styles for better control */
.transition-overlay {
  /* Only non-background styles here */
}

.overlay-content {
  text-align: center;
  max-width: 500px;
  width: 90%;
  padding: 2rem;
}

.module-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.module-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  animation: pulse-icon 2s ease-in-out infinite;
}

.module-icon i {
  font-size: 28px;
  color: white;
}

/* Ensure module icon stays consistent in both modes */
.transition-overlay.dark-mode .module-icon {
  /* Background is set via :style prop, so it's preserved */
}

.transition-overlay.dark-mode .module-icon i {
  color: white !important;
}

.module-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.dark-mode .module-title {
  color: #f9fafb;
}

.loading-message {
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 2rem;
  font-weight: 500;
}

.dark-mode .loading-message {
  color: #9ca3af;
}

.skeleton-container {
  margin-bottom: 2rem;
  opacity: 0.7;
}

.skeleton-workspace {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.transition-overlay.dark-mode .skeleton-workspace {
  background: #374151 !important;
  border-color: #4b5563 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3) !important;
}

/* Header Section */
.skeleton-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.transition-overlay.dark-mode .skeleton-header {
  border-color: #4b5563 !important;
}

.skeleton-actions {
  display: flex;
  gap: 0.5rem;
}

.skeleton-button {
  height: 32px;
  width: 80px;
  background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
  border-radius: 6px;
  animation: skeleton-wave 1.5s ease-in-out infinite;
}

.skeleton-button.small {
  width: 60px;
  height: 28px;
}

.transition-overlay.dark-mode .skeleton-button {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 50%, #4b5563 100%) !important;
}

/* Main Content Layout */
.skeleton-content {
  display: flex;
  gap: 1.5rem;
  min-height: 200px;
}

/* Sidebar Section */
.skeleton-sidebar {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-right: 1rem;
  border-right: 1px solid #e5e7eb;
}

.transition-overlay.dark-mode .skeleton-sidebar {
  border-color: #4b5563 !important;
}

.skeleton-workspace-title {
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.transition-overlay.dark-mode .skeleton-workspace-title {
  border-color: #4b5563 !important;
}

.skeleton-space-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.skeleton-nested {
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Main Task Area */
.skeleton-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.transition-overlay.dark-mode .skeleton-task-header {
  border-color: #4b5563 !important;
}

.skeleton-filters {
  display: flex;
  gap: 0.5rem;
}

.skeleton-task-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeleton-task-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
}

.transition-overlay.dark-mode .skeleton-task-row {
  background: #4b5563 !important;
  border-color: #6b7280 !important;
}

.transition-overlay.dark-mode .skeleton-checkbox {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 50%, #4b5563 100%) !important;
}

.skeleton-checkbox {
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
  border-radius: 4px;
  animation: skeleton-wave 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.skeleton-task-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.skeleton-task-meta {
  flex-shrink: 0;
}

.skeleton-line {
  height: 8px;
  background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
  border-radius: 4px;
  animation: skeleton-wave 1.5s ease-in-out infinite;
}

.transition-overlay.dark-mode .skeleton-line {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 50%, #4b5563 100%) !important;
}

.skeleton-line.short {
  width: 30%;
}

.skeleton-line.medium {
  width: 50%;
}

.skeleton-line.long {
  width: 80%;
}

.skeleton-line.full {
  width: 100%;
}

.progress-container {
  margin-bottom: 1.5rem;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.transition-overlay.dark-mode .progress-bar {
  background: #4b5563 !important;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 2px;
  transition: width 0.3s ease;
  animation: progress-shimmer 2s ease-in-out infinite;
}

/* Keep progress fill gradient consistent in both modes */
.transition-overlay.dark-mode .progress-fill {
  background: linear-gradient(90deg, #6366f1, #8b5cf6) !important;
}

.progress-text {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.transition-overlay.dark-mode .progress-text {
  color: #9ca3af !important;
}

.success-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  color: white;
  font-weight: bold;
  font-size: 12px;
  animation: success-pop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.transition-overlay.dark-mode .success-icon {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}

@keyframes success-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6366f1;
  animation: dot-bounce 1.4s ease-in-out infinite both;
}

/* Keep loading dots color consistent in both modes */
.transition-overlay.dark-mode .dot {
  background: #6366f1 !important;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

.dot:nth-child(3) {
  animation-delay: 0s;
}

/* Animations */
.overlay-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.overlay-fade-leave-active {
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.overlay-fade-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
  backdrop-filter: blur(0px);
}

.overlay-fade-leave-to {
  opacity: 0;
  transform: scale(1.05) translateY(-10px);
  backdrop-filter: blur(0px);
}

/* Transitions now use inline styles, so these are not needed */

/* Success animation when completing */
.overlay-content.completing {
  animation: success-bounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes success-bounce {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  }
}

@keyframes skeleton-wave {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

@keyframes progress-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

@keyframes dot-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* Additional dark mode styles for new elements */
.dark-mode .skeleton-space-item {
  /* No background change needed, inherits from parent */
}

.dark-mode .skeleton-nested {
  /* No background change needed, inherits from parent */
}

.dark-mode .skeleton-main {
  /* No background change needed, it's a flex container */
}

.dark-mode .skeleton-task-list {
  /* No background change needed, it's a flex container */
}

.dark-mode .skeleton-task-content {
  /* No background change needed, it's a flex container */
}

.dark-mode .skeleton-task-meta {
  /* No background change needed, it's a flex container */
}

.dark-mode .skeleton-filters {
  /* No background change needed, it's a flex container */
}

.dark-mode .skeleton-actions {
  /* No background change needed, it's a flex container */
}

.dark-mode .skeleton-content {
  /* No background change needed, it's a flex container */
}

/* Responsive */
@media (max-width: 768px) {
  .overlay-content {
    padding: 1rem;
    width: 95%;
  }

  .module-icon {
    width: 56px;
    height: 56px;
  }

  .module-icon i {
    font-size: 24px;
  }

  .module-title {
    font-size: 1.25rem;
  }

  .loading-message {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .skeleton-workspace {
    padding: 1rem;
    max-width: 100%;
  }

  .skeleton-content {
    flex-direction: column;
    gap: 1rem;
    min-height: auto;
  }

  .skeleton-sidebar {
    flex: none;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding-right: 0;
    padding-bottom: 1rem;
  }

  .transition-overlay.dark-mode .skeleton-sidebar {
    border-color: #4b5563 !important;
  }

  .transition-overlay.dark-mode .skeleton-button {
    background: linear-gradient(90deg, #4b5563 0%, #6b7280 50%, #4b5563 100%) !important;
  }

  .transition-overlay.dark-mode .skeleton-button.small {
    background: linear-gradient(90deg, #4b5563 0%, #6b7280 50%, #4b5563 100%) !important;
  }

  .skeleton-task-row {
    padding: 0.5rem;
    flex-wrap: wrap;
  }

  .skeleton-task-content {
    min-width: 0;
  }

  .skeleton-actions {
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .skeleton-button {
    width: 60px;
    height: 28px;
  }

  .skeleton-button.small {
    width: 50px;
    height: 24px;
  }
}

@media (max-width: 480px) {
  .overlay-content {
    padding: 0.75rem;
  }

  .module-header {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .skeleton-workspace {
    padding: 0.75rem;
  }

  .skeleton-task-row {
    padding: 0.5rem;
    gap: 0.5rem;
  }

  .progress-container {
    margin-bottom: 1rem;
  }
}
</style>